/* eslint-disable react/jsx-props-no-spreading */
// @ts-nocheck
import React, { useState } from 'react';
import { useGetProjectsQuery } from '../../services/projects.service';
import { useGetUserPermissionsIdsQuery } from '../../services/user-permissions.service';
import SelectTypeAhead, { normalizeOptionValue } from '../../components/form-elements/select-type-ahead';

interface Props {
  hidden?: boolean;
  disabled?: boolean;
  value?: { label: string; value: string };
  onChange: (newProject: string | null) => void;
  label: string;
  apiToken?: string;
  objectId?: any;
  requestType?: string;
  filterByUserAccess?: boolean;
  hasValidationError?: boolean;
  helperText?: string;
  clearProjectOption?: {label: string, value: string};
}

interface OptionType {
  value: string;
  label: string;
  disable?: boolean;
  disableText?: string;
  tooltipText?: string;
}

export const useProjectOptions = (
  apiToken: string,
  objectId: string,
  requestType?: string,
  filterByUserAccess?: boolean,
  search: string = '',
) => {
  const { data: userPermissions } = useGetUserPermissionsIdsQuery({
    apiToken,
    objectId,
    requestType,
  }, {
    skip: !filterByUserAccess || !objectId,
  });

  const {
    data: projects,
    isError,
    isFetching,
  } = useGetProjectsQuery(
    {
      search,
      per_page: 1000,
      sort: 'name',
      sort_direction: 'ASC',
    },
    {
      enabled: !!search,
    },
  );

  const filterPermissions = (project: Record<any, any>) => {
    const isAccessible = userPermissions?.userAccessibleProjectIds.includes(
      project.id,
    );
    return {
      value: project.id,
      label: project.name,
      disabled: !isAccessible,
      tooltipText: isAccessible ? '' : 'Add assignee to this project to enable',
    };
  };

  const mapProjects = (project: Record<any, any>) => ({
    value: project.id,
    label: project.name,
    disabled: false,
    tooltipText: '',
  });

  const projectOpts: OptionType[] = projects?.projects.map(filterByUserAccess ? filterPermissions : mapProjects) || [];

  return { projectOpts, isError, isFetching };
};

const ProjectField: React.FC<Props> = ({
  hidden = false,
  disabled = false,
  value,
  onChange,
  label,
  apiToken,
  objectId,
  requestType,
  filterByUserAccess = false,
  hasValidationError,
  helperText,
  clearProjectOption,
}) => {
  const [search, setSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(normalizeOptionValue(value));
  const { projectOpts, isError, isFetching } = useProjectOptions(
    apiToken, objectId, requestType, filterByUserAccess, search,
  );

  const projectOptions = clearProjectOption && projectOpts ? [clearProjectOption, ...projectOpts] : projectOpts;

  const handleChoiceChange = (event: any, newProjectValue: OptionType) => {
    setSelectedProject(newProjectValue);
    onChange(newProjectValue?.value || null);
  };

  return (
    <SelectTypeAhead
      hidden={hidden}
      label={label}
      isError={isError}
      errorResource="project"
      options={projectOptions}
      onSetFilter={setSearch}
      loading={isFetching}
      disabled={disabled}
      value={selectedProject}
      onChange={handleChoiceChange}
      onClose={() => setSearch('')}
      isOptionEqualToValue={(option: OptionType, val: OptionType) => option.value === val.value}
      filterOptions={(options) => options}
      hasValidationError={hasValidationError}
      helperText={helperText}
    />
  );
};

export default ProjectField;
